import Service from 'react-reservice';

import userPreferenceApi, {
  EUserPreferenceCategory,
  EUserPreferenceType,
  IGetUserPreferenceResponse,
  IUpdateUserPreferenceParam
} from '@webtrader/core/api/user/preference';
import { EEventMessageType } from '@webtrader/core/chart/events';
import { ICandlestickColorUserPreference } from '@webtrader/core/constants/candlestick/type';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { chartService } from '@webtrader/core/services/chart';
import { EVolumeMode, EVolumeModeText } from '@webtrader/core/services/form/order/service';
import storage, { EStorageKey } from '@webtrader/core/storage';

import { themeService } from '../theme/v2';

interface IUserPreferenceContext {
  loadingGetUserPreference: boolean;
  loadingGetCandlestickColorUserPreference: boolean;
  loadingUpdateUserPreference: boolean;
  loadingUpdateUserPreferenceOCT: boolean;
  userPreference: IGetUserPreferenceResponse[];
  lineDrawingData: string;
  isUserOCTEnable: boolean;
  volumeModePreference: EVolumeMode | undefined;
  isTradeDetailsPinned: boolean;
  dealerUserPreference?: IGetUserPreferenceResponse;
  candlestickColorUserPreference?: ICandlestickColorUserPreference;
}

const defaultContext: IUserPreferenceContext = {
  loadingGetUserPreference: false,
  loadingGetCandlestickColorUserPreference: false,
  loadingUpdateUserPreference: false,
  loadingUpdateUserPreferenceOCT: false,
  userPreference: [],
  lineDrawingData: '',
  isUserOCTEnable: false,
  volumeModePreference: undefined,
  isTradeDetailsPinned: false,
  dealerUserPreference: undefined,
  candlestickColorUserPreference: undefined
};

export interface IUpdateUserPreferenceParams extends IUpdateUserPreferenceParam {
  onComplete?: (success: boolean, message?: string) => void;
  showLoading?: boolean;
}

export interface IUpdateVolumeModePreferenceParams {
  value: EVolumeMode;
  params: IUpdateUserPreferenceParams;
}

export interface IUpdateCandlestickColorUserPreferenceParams {
  value: ICandlestickColorUserPreference;
  params: IUpdateUserPreferenceParams;
}

class UserPreferenceService extends Service<IUserPreferenceContext> {
  private getDealerUserPreferenceAC?: AbortController;

  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public getSwapUnitUserPreference = async () => {
    // Fallback if error occurs during fetching user preference , also to prevent the Volume field from flickering during the fetching process
    const volumeModePreference = storage.getItem<EVolumeMode>(EStorageKey.VOLUME_MODE_PREFERENCE);

    this.context.volumeModePreference = volumeModePreference ?? EVolumeMode.SIZE;

    try {
      const { result } = await userPreferenceApi.getUserPreference({
        type: EUserPreferenceType.SWAP_UNITS,
        category: EUserPreferenceCategory.SWAP_UNITS
      });
      const userPreference = result || [];
      this.checkVolumeModePreference(userPreference);
      return userPreference;
    } catch (error) {
      const { code, message } = error as IApiError;
      return mapResponseCodeToMessage(code, message);
    }
  };

  public getOCTUserPreference = async (loading = false) => {
    try {
      this.context.loadingGetUserPreference = loading;

      const { result } = await userPreferenceApi.getUserPreference({
        type: EUserPreferenceType.OCT_ENABLE,
        category: EUserPreferenceCategory.ONE_CLICK_TRADING
      });
      const userPreference = result || [];

      this.checkOCTEnable(userPreference);
      return userPreference;
    } catch (error) {
      const { code, message } = error as IApiError;
      return mapResponseCodeToMessage(code, message);
    } finally {
      this.context.loadingGetUserPreference = false;
      this.context.loadingUpdateUserPreferenceOCT = false;
    }
  };

  public getLineDrawingUserPreference = async (symbol?: string) => {
    try {
      const { result } = await userPreferenceApi.getUserPreference({
        type: EUserPreferenceType.LINE_DRAWING,
        category: EUserPreferenceCategory.LINE_DRAWING,
        symbol
      });
      const lineDrawing =
        result?.find(
          (p) => p.category === EUserPreferenceCategory.LINE_DRAWING && p.type === EUserPreferenceType.LINE_DRAWING
        )?.value ?? '';

      if (lineDrawing && JSON.parse(lineDrawing).length > 0) {
        chartService.postEventMessage({
          type: EEventMessageType.OPEN_DRAWING,
          data: lineDrawing
        });
      }
      this.context.lineDrawingData = lineDrawing;
      return lineDrawing;
    } catch (error) {
      const { code, message } = error as IApiError;
      return mapResponseCodeToMessage(code, message);
    }
  };

  public getTradeDetailsUserPreference = async () => {
    try {
      const { result } = await userPreferenceApi.getUserPreference({
        type: EUserPreferenceType.PTP_ENABLE,
        category: EUserPreferenceCategory.PRE_TRADE_PIN
      });
      const userPreference = result || [];

      this.checkIsTradeDetailsPinned(userPreference);
      return userPreference;
    } catch (error) {
      const { code, message } = error as IApiError;
      return mapResponseCodeToMessage(code, message);
    }
  };

  public getDealerUserPreference = async () => {
    this.context.loadingGetUserPreference = true;

    try {
      this.getDealerUserPreferenceAC?.abort();
      this.getDealerUserPreferenceAC = new AbortController();

      const { result } = await userPreferenceApi.getUserPreference(
        {
          type: EUserPreferenceType.DEALER_FILTER_TYPES,
          category: EUserPreferenceCategory.DEALER
        },
        this.getDealerUserPreferenceAC.signal
      );

      this.context.dealerUserPreference = result?.length ? result[0] : undefined;
    } catch (error) {
      const { code, message } = error as IApiError;

      throw new Error(mapResponseCodeToMessage(code, message));
    } finally {
      this.context.loadingGetUserPreference = false;
    }
  };

  public updateVolumeModePreference = async ({ value, params }: IUpdateVolumeModePreferenceParams) => {
    this.context.volumeModePreference = value;
    await this.updateUserPreference(params);
    storage.setItem(EStorageKey.VOLUME_MODE_PREFERENCE, value);
  };

  public updateUserPreference = async ({
    category,
    type,
    value,
    onComplete,
    showLoading = true
  }: IUpdateUserPreferenceParams) => {
    try {
      if (category === EUserPreferenceCategory.ONE_CLICK_TRADING) {
        this.context.loadingUpdateUserPreferenceOCT = true;
      } else {
        this.context.loadingUpdateUserPreference = showLoading;
      }

      await userPreferenceApi.updateUserPreference({
        category,
        type,
        value
      });

      onComplete?.(true);
    } catch (error) {
      if (category === EUserPreferenceCategory.ONE_CLICK_TRADING) {
        this.context.loadingUpdateUserPreferenceOCT = false;
      }
      this.context.loadingUpdateUserPreference = false;

      const { code, message } = error as IApiError;
      onComplete?.(false, mapResponseCodeToMessage(code, message));
    } finally {
      this.context.loadingUpdateUserPreference = false;
    }
  };

  private checkVolumeModePreference = (userPreference: IGetUserPreferenceResponse[]) => {
    const swapUnitsObject = userPreference.find(
      (p) => p.category === EUserPreferenceCategory.SWAP_UNITS && p.type === EUserPreferenceType.SWAP_UNITS
    )?.value;

    let volumeMode;
    if (swapUnitsObject) {
      if (swapUnitsObject === EVolumeModeText.SIZE) {
        volumeMode = EVolumeMode.SIZE;
        storage.setItem(EStorageKey.VOLUME_MODE_PREFERENCE, EVolumeMode.SIZE);
      } else if (swapUnitsObject === EVolumeModeText.UNITS) {
        volumeMode = EVolumeMode.UNITS;
        storage.setItem(EStorageKey.VOLUME_MODE_PREFERENCE, EVolumeMode.UNITS);
      } else {
        volumeMode = undefined;
      }
    }

    // If there is no preference, set default to SIZE
    if (volumeMode === undefined) {
      this.updateVolumeModePreference({
        value: EVolumeMode.SIZE,
        params: {
          category: EUserPreferenceCategory.SWAP_UNITS,
          type: EUserPreferenceType.SWAP_UNITS,
          value: EVolumeModeText.SIZE,
          showLoading: false
        }
      });
      return;
    }

    this.context.volumeModePreference = volumeMode;
  };

  private checkOCTEnable = (userPreference: IGetUserPreferenceResponse[]) => {
    const octObject = userPreference.find(
      (p) => p.category === EUserPreferenceCategory.ONE_CLICK_TRADING && p.type === EUserPreferenceType.OCT_ENABLE
    )?.value;

    this.context = {
      ...this.context,
      isUserOCTEnable: octObject && octObject === 'true' ? true : false
    };
  };

  private checkIsTradeDetailsPinned = (userPreference: IGetUserPreferenceResponse[]) => {
    let isTradeDetailsPinned;
    const tradeDetailUserPreference = userPreference.find(
      (p) => p.category === EUserPreferenceCategory.PRE_TRADE_PIN && p.type === EUserPreferenceType.PTP_ENABLE
    )?.value;

    if (
      tradeDetailUserPreference === undefined ||
      (tradeDetailUserPreference && tradeDetailUserPreference === 'true')
    ) {
      isTradeDetailsPinned = true;
    } else {
      isTradeDetailsPinned = false;
    }

    this.context.isTradeDetailsPinned = isTradeDetailsPinned;
  };

  public getDealerUserPreferenceType = () => {
    return this.context.dealerUserPreference?.value;
  };

  public getCandlestickColorUserPreference = async (loading = false) => {
    try {
      this.context.loadingGetCandlestickColorUserPreference = loading;
      const {
        positive: defaultCandlestickPositive,
        negative: defaultCandlestickNegative
      } = themeService.context.theme.colors?.candlestick;

      const { result } = await userPreferenceApi.getUserPreference({
        type: EUserPreferenceType.CANDLESTICK_COLOR,
        category: EUserPreferenceCategory.CANDLESTICK
      });
      const candlestickColorUserPreference = result?.length ? JSON.parse(result[0].value) : undefined;
      const { positive, negative } = candlestickColorUserPreference || {};
      const positiveValue = positive ?? defaultCandlestickPositive;
      const negativeValue = negative ?? defaultCandlestickNegative;

      const updatedCandlestickColor = { positive: positiveValue, negative: negativeValue };
      this.context.candlestickColorUserPreference = updatedCandlestickColor;

      return updatedCandlestickColor;
    } catch (error) {
      const { code, message } = error as IApiError;
      throw new Error(mapResponseCodeToMessage(code, message));
    } finally {
      this.context.loadingGetCandlestickColorUserPreference = false;
    }
  };

  public updateCandlestickColorUserPreference = async ({
    value,
    params
  }: IUpdateCandlestickColorUserPreferenceParams) => {
    this.context.candlestickColorUserPreference = value;
    await this.updateUserPreference(params);
  };
}

export default new UserPreferenceService();
